import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useAppDispatch, useAppSelector} from '../../../redux';
import {getFormValues, SubmissionError, InjectedFormProps, reduxForm, FormErrors, initialize, change} from 'redux-form';

import View from './view';
import {LoginState} from '../../../shared/models/login/loginStep';
import {Forms} from '../../../shared/models/forms';
import {RegistrationOrRecoveryForm} from '../../../shared/models/login';
import {validationPipe, FormValidationError, ValidationItem} from '../../../shared/pipes';
import {InputMaskTypes} from '../../../shared/components/baseField';
import {
  registration,
  createNewPassword,
  getNewCode,
  checkCode,
  setLoginResponse,
  setLoginValue,
} from '../../../redux/actions/login';
import {setRuntimeLoginStepState} from '../../../redux/actions/runtime/loginStep';
import {LoginStepSelector} from '../../../redux/selectors/runtime/loginStep';
import {useActiveForm} from '../../../shared/components/activeFormContext';
import {loginResponseSelector} from '../../../redux/selectors/login';
import {
  LoginResponsePayload,
  LoginType,
  RequestedOperationType,
  SendCodeChannel,
  SendCodeChannelType,
} from '../../../shared/models/api';
import {onlyDigitsPipe} from '../../../shared/pipes';
import {defaults} from '../../../shared/constants/defaults';
import {RegExps} from '../../../shared/constants/regexps';
import {passwordValidator, passwordValidatorMessage} from '../../../shared/utils/validators';
import {ServerValidationError} from '../../../shared/models/serverValidationError';
import {startDigits} from '../../../shared/constants';

const getFormErrors = (formData: RegistrationOrRecoveryForm, step: LoginState) => {
  if (step === LoginState.forgotPasswordNotCard) {
    let startCardDigits = (formData?.cardNumber ?? '').startsWith(startDigits);
    let isMaxCardDigitsLength = (formData?.cardNumber ?? '').length === 16;
    const baseValidationArray: ValidationItem[] = [
      {
        names: 'cardNumber',
        error: 'Обязательное поле',
        type: 'required',
      },
    ];
    if (!startCardDigits) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: `Укажите номер карты (номер должен начинаться с ${startDigits})`,
        type: 'pattern',
        pattern: new RegExp(RegExps.checkEsksoCardNumber),
      });
    } else if (!isMaxCardDigitsLength) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: 'Длина номера должна равняться 16 символам',
        type: 'pattern',
        pattern: new RegExp(RegExps.checkEsksoCardNumber),
      });
    }
    return validationPipe(formData, baseValidationArray);
  } else if (step === LoginState.registrationCode || step === LoginState.forgotPasswordCode) {
    return validationPipe(formData, [
      {
        names: 'smsCode',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'smsCode',
        error: `Код должен состоять из ${defaults.smsCodeLength} цифр`,
        type: 'pattern',
        pattern: RegExps.checkSmsCode,
      },
    ]);
  } else if (step !== LoginState.agreement && step !== LoginState.forgotPassword) {
    return validationPipe(formData, [
      {
        names: 'password',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'password',
        error: passwordValidatorMessage,
        type: 'custom',
        validator: passwordValidator,
      },
      {
        names: 'confirm',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'confirm,password',
        error: 'Поле не совпадает с паролем',
        type: 'equals',
      },
    ]);
  }
};

const Component = (props: InjectedFormProps) => {
  const {handleSubmit} = props;

  const dispatch = useAppDispatch();
  const step = useSelector(LoginStepSelector());
  const loginResponse = useSelector(loginResponseSelector());
  const recoveryChannel = React.useRef<SendCodeChannelType | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const formValues: RegistrationOrRecoveryForm = useAppSelector((state) =>
    getFormValues(Forms.registrationOrRecoveryForm)(state),
  ) as RegistrationOrRecoveryForm;
  const mask =
    loginResponse?.loginType === LoginType.PhoneNumber
      ? InputMaskTypes.tel
      : loginResponse?.loginType === LoginType.EskNumber
      ? InputMaskTypes.card
      : InputMaskTypes.snils;

  useEffect(() => {
    if (step === LoginState.forgotPassword) {
      if (loginResponse?.loginType !== LoginType.EskNumber) {
        dispatch(setRuntimeLoginStepState(LoginState.forgotPasswordNotCard));
      }
    }
  }, []);

  const {setActiveForm} = useActiveForm();

  useEffect(() => {
    switch (step) {
      case LoginState.forgotPasswordPasswords:
      case LoginState.registrationPasswords:
        setActiveForm(Forms.registrationOrRecoveryForm);
        break;
      default:
        setActiveForm();
        break;
    }
    return setActiveForm;
  }, [step]);

  const onMainClick = async (formData: RegistrationOrRecoveryForm) => {
    const submissionError = getFormErrors(formData, step);
    if (submissionError) {
      throw new SubmissionError(submissionError);
    }
    if (step === LoginState.forgotPassword || step === LoginState.forgotPasswordNotCard) {
      const channel = recoveryChannel.current ?? SendCodeChannel.Sms;
      recoveryChannel.current = null;
      const cardNumber = step === LoginState.forgotPassword ? formData?.identificator : formData?.cardNumber;

      try {
        setSubmitting(true);
        await dispatch(getNewCode(onlyDigitsPipe(cardNumber ?? ''), false, channel));
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
        if (!formData.cardNumber) {
          throw new SubmissionError({
            identificator: new FormValidationError(errorText, true),
          } as FormErrors<any, FormValidationError>);
        } else {
          throw new SubmissionError({
            cardNumber: new FormValidationError(errorText, true),
          } as FormErrors<any, FormValidationError>);
        }
      } finally {
        setSubmitting(false);
      }
      if (step === LoginState.forgotPasswordNotCard) {
        const newLoginResponse: LoginResponsePayload = {...loginResponse};
        newLoginResponse.loginType = 'EskNumber';
        dispatch(setLoginResponse(newLoginResponse));
        dispatch(setLoginValue(formData?.cardNumber || ''));
        dispatch(initialize(Forms.registrationOrRecoveryForm, {identificator: formData?.cardNumber}));
      }

      if (channel === SendCodeChannel.Email) {
        dispatch(setRuntimeLoginStepState(LoginState.forgotPasswordEmail));
      } else {
        dispatch(setRuntimeLoginStepState(LoginState.forgotPasswordCode));
      }
    } else if (step === LoginState.forgotPasswordCode) {
      try {
        setSubmitting(true);
        await dispatch(checkCode(formData.smsCode || '', '', RequestedOperationType.SetPasswordConfirmation));
        dispatch(setRuntimeLoginStepState(LoginState.forgotPasswordPasswords));
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
        throw new SubmissionError({
          smsCode: new FormValidationError(errorText, true),
        } as FormErrors<any, FormValidationError>);
      } finally {
        setSubmitting(false);
      }
    } else if (step === LoginState.forgotPasswordPasswords) {
      try {
        setSubmitting(true);
        await dispatch(createNewPassword(formData.smsCode || '', formData.password || '', false));
      } catch {
      } finally {
        setSubmitting(false);
      }
    } else if (step === LoginState.registrationCode) {
      try {
        setSubmitting(true);
        await dispatch(checkCode(formData.smsCode || '', '', RequestedOperationType.SetPasswordConfirmation));
        dispatch(setRuntimeLoginStepState(LoginState.registrationPasswords));
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
        throw new SubmissionError({
          smsCode: new FormValidationError(errorText, true),
        } as FormErrors<any, FormValidationError>);
      } finally {
        setSubmitting(false);
      }
    } else if (step === LoginState.registrationPasswords) {
      try {
        setSubmitting(true);
        await dispatch(registration(formData.password || '', formData.smsCode || ''));
      } finally {
        setSubmitting(false);
      }
    } else if (step === LoginState.agreement) {
      try {
        setSubmitting(true);
        await dispatch(getNewCode(onlyDigitsPipe(formData?.identificator || ''), true, SendCodeChannel.Sms));
        dispatch(setRuntimeLoginStepState(LoginState.registrationCode));
      } catch {
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleRecoveryByEmail = async () => {
    recoveryChannel.current = SendCodeChannel.Email;
    handleSubmit(onMainClick)();
  };

  const [disabled, setDisabled] = React.useState(false);
  React.useEffect(() => {
    setDisabled(
      ((step === LoginState.forgotPasswordCode || step === LoginState.registrationCode) && !formValues.smsCode) ||
        (step === LoginState.agreement && !formValues.agreement) ||
        false,
    );
  }, [step, formValues]);

  const handleAutoFill = (data: any) => {
    dispatch(change(Forms.registrationOrRecoveryForm, 'cardNumber', data));
  };

  return (
    <View
      onAutoFill={handleAutoFill}
      disabled={disabled}
      step={step}
      onSubmit={(e) => {
        e?.preventDefault();
        handleSubmit(onMainClick)();
      }}
      onRefresh={async (channelType: SendCodeChannelType = SendCodeChannel.Sms) => {
        try {
          await dispatch(getNewCode(onlyDigitsPipe(formValues?.identificator || ''), true, channelType));
          return true;
        } catch {
          return false;
        }
      }}
      onRecoveryByEmail={handleRecoveryByEmail}
      mask={mask}
      loginType={loginResponse?.loginType}
      submitting={submitting}
    />
  );
};

export const RegistrationOrRecovery = reduxForm<RegistrationOrRecoveryForm, {}>({
  form: Forms.registrationOrRecoveryForm,
})(Component);
